<template>
  <AiEducationLayout :menu-items="menuItems">
    <EducationPageHeader title="AI 学伴" description="基于当前课程授权知识库进行学习问答" />
    <ContentWrap class="assistant-card">
      <el-select
        v-model="courseId"
        class="course-select"
        placeholder="选择课程"
        :loading="coursesLoading"
      >
        <el-option
          v-for="course in courses"
          :key="course.id"
          :label="course.name || '未命名课程'"
          :value="course.id"
        />
      </el-select>

      <EducationState
        v-if="!coursesLoading && !courses.length"
        state="empty"
        title="暂无可用课程"
        description="获得课程授权后才能使用 AI 学伴。"
      />
      <div v-else class="chat-area">
        <div v-if="!answer && !asking" class="welcome">
          <Icon icon="ep:magic-stick" :size="34" />
          <strong>选择课程并提出学习问题</strong>
          <span>回答只引用当前课程授权知识库；没有可靠来源时系统会拒绝回答。</span>
        </div>
        <div v-if="lastQuestion" class="message question">{{ lastQuestion }}</div>
        <div v-if="answer || asking" class="message answer">
          <div class="answer-label">AI 学伴</div>
          <p>{{ answer || '正在检索课程知识库…' }}</p>
          <div v-if="citations.length" class="citations">
            <strong>引用来源</strong>
            <div
              v-for="(citation, index) in citations"
              :key="`${citation.documentId}-${citation.segmentId}`"
            >
              <span>[{{ index + 1 }}]</span>{{ citation.content }}
            </div>
          </div>
          <el-alert v-if="refusalReason" :title="refusalReason" type="warning" :closable="false" />
        </div>
      </div>

      <div class="composer">
        <el-input
          v-model="question"
          type="textarea"
          :rows="3"
          maxlength="2000"
          show-word-limit
          placeholder="输入课程相关问题"
          :disabled="!courseId || asking"
          @keydown.ctrl.enter="ask"
        />
        <div class="composer-actions">
          <el-button v-if="asking" type="danger" plain @click="stop">停止</el-button>
          <el-button v-else-if="lastQuestion" plain @click="retry">重试</el-button>
          <el-button type="primary" :disabled="!courseId || !question.trim() || asking" @click="ask"
            >发送</el-button
          >
        </div>
      </div>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import type { EducationMenuItem } from '../layout/types'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import { getAccessToken, getTenantId } from '@/utils/auth'
import { getEducationMembershipId } from '../composables/educationMembershipContext'
import {
  getMyStudentCoursePage,
  getStudentCourseContext,
  type StudentAiCitationVO,
  type StudentCourseVO
} from '@/api/aiEducation'

defineOptions({ name: 'AiEducationStudentAssistant' })
const courses = ref<StudentCourseVO[]>([])
const coursesLoading = ref(true)
const courseId = ref<number>()
const question = ref('')
const lastQuestion = ref('')
const answer = ref('')
const refusalReason = ref('')
const citations = ref<StudentAiCitationVO[]>([])
const asking = ref(false)
let controller: AbortController | undefined

const menuItems: EducationMenuItem[] = []

const loadCourses = async () => {
  try {
    courses.value = (await getMyStudentCoursePage()).list || []
  } finally {
    coursesLoading.value = false
  }
}
const handleEvent = (name: string, data: string) => {
  const payload = JSON.parse(data)
  if (name === 'content') answer.value += payload.content || ''
  if (name === 'citation' && payload.citation) citations.value.push(payload.citation)
  if (name === 'finish') refusalReason.value = payload.refusalReason || ''
  if (name === 'error') throw new Error(payload.errorMessage || 'AI 问答服务暂不可用')
}
const ask = async () => {
  const text = question.value.trim()
  if (!courseId.value || !text || asking.value) return
  lastQuestion.value = text
  answer.value = ''
  refusalReason.value = ''
  citations.value = []
  asking.value = true
  controller = new AbortController()
  try {
    const context = await getStudentCourseContext(courseId.value)
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL}/app-api/ai-education/student/ai/ask-stream`,
      {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
          'tenant-id': String(getTenantId() || ''),
          ...(getEducationMembershipId()
            ? { 'ai-education-membership-id': String(getEducationMembershipId()) }
            : {})
        },
        body: JSON.stringify({
          schoolId: context.schoolId,
          trainCourseId: courseId.value,
          question: text
        })
      }
    )
    if (!response.ok || !response.body) throw new Error('AI 问答请求失败')
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() || ''
      for (const block of blocks) {
        const event =
          block
            .split('\n')
            .find((line) => line.startsWith('event:'))
            ?.slice(6)
            .trim() || 'message'
        const data = block
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('\n')
        if (data) handleEvent(event, data)
      }
      if (done) break
    }
    question.value = ''
  } catch (error) {
    if ((error as Error).name !== 'AbortError') refusalReason.value = (error as Error).message
  } finally {
    asking.value = false
    controller = undefined
  }
}
const stop = () => controller?.abort()
const retry = () => {
  question.value = lastQuestion.value
  ask()
}
onMounted(loadCourses)
onBeforeUnmount(stop)
</script>

<style scoped>
.assistant-card {
  min-height: 560px;
}
.course-select {
  width: min(360px, 100%);
}
.chat-area {
  min-height: 330px;
  padding: 24px 0;
}
.welcome {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #19705d;
  text-align: center;
}
.welcome span {
  max-width: 500px;
  color: #697681;
}
.message {
  max-width: 760px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 8px;
  line-height: 1.7;
}
.question {
  margin-left: auto;
  background: #eaf5f1;
}
.answer {
  border: 1px solid #e5e9ec;
  background: #fff;
}
.answer-label,
.citations > strong {
  color: #19705d;
  font-weight: 600;
}
.citations {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #edf0f2;
  color: #697681;
  font-size: 13px;
}
.citations div {
  margin-top: 8px;
}
.citations span {
  margin-right: 6px;
  color: #19705d;
}
.composer {
  border-top: 1px solid #e5e9ec;
  padding-top: 16px;
}
.composer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
</style>
